import { toast } from "sonner";
import type { ErrorInfo, ReactNode } from "react";
import type { ChatTool } from "../types/chat";
import { Component, useEffect, useMemo, useRef, useState } from "react";
import type { McpTools } from "../api/models/mcpTools";
import { getTradingOrders } from "../api/endpoints/trading/trading";
import { toNumber } from "../lib/numberFormat";
// Mesma validação de estado de futuros usada na tela /futuros
// (`FuturesPositionsPage`). Fonte única — o widget não duplica a regra para não
// divergir (ex.: botão "Fechar" some no chat mas aparece em /futuros). Os
// aliases preservam o vocabulário local do widget.
import {
  isClosedPosition as isClosedLinearPosition,
  isOpenPosition as isOpenLinearPosition,
  isPendingOrder as isCancellableOrder,
} from "../lib/linearOrders";
import { QRCodeSVG } from "qrcode.react";
import { useNavigate } from "react-router-dom";

import "./tool_ui.css";

type ToolRendererCtx = {
  tool: ChatTool;
  isLocked: boolean;
  children?: ReactNode;
  isExpanded: boolean;
  agentIsRunning: boolean;
  onToggleExpanded: () => void;
  sendUserMessage: (content: string) => Promise<unknown>;
};

type ToolRenderer = (props: ToolRendererCtx) => ReactNode;

/**
 * Extracts and types the tool output using `McpTools[tool_name]['output']`.
 * Handles the optional `structured_content` wrapper from the agent.
 */
function getToolOutput<T extends keyof McpTools>(tool: ChatTool) {
  const raw = tool.tool_end?.output;

  if (!raw || typeof raw !== "object") {
    return undefined;
  }

  const output = "structured_content" in raw
    ? raw.structured_content
    : raw;

  return output as McpTools[T]["output"] | undefined;
}

/**
 * Extracts and types the tool input using `McpTools[tool_name]['input']`.
 */
function getToolInput<T extends keyof McpTools>(tool: ChatTool) {
  const raw = tool.tool_start?.input;

  if (!raw || typeof raw !== "object") {
    return undefined;
  }

  return raw as "input" extends keyof McpTools[T] ? McpTools[T]["input"] : undefined;
}

function formatPrice(price: string): string {
  const num = parseFloat(price);

  return num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatVolume(volume: string): string {
  const num = parseFloat(volume);
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(2)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(2)}K`;
  return num.toFixed(2);
}

function formatPercent(pct: string): string {
  const num = parseFloat(pct);
  return `${num >= 0 ? "+" : ""}${num.toFixed(2)}%`;
}

function Sparkline({
  data,
  isPositive,
}: {
  data: number[];
  isPositive: boolean;
}) {
  if (data.length < 2) return null;

  const W = 280;
  const H = 60;
  const PAD = 2;
  const values = data;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  const points = values
    .map((v, i) => {
      const x = PAD + (i / (values.length - 1)) * (W - PAD * 2);
      const y = PAD + (1 - (v - min) / range) * (H - PAD * 2);
      return `${x},${y}`;
    })
    .join(" ");

  const stroke = isPositive ? "#22c55e" : "#ef4444";
  const fill = isPositive ? "rgba(34,197,94,0.10)" : "rgba(239,68,68,0.10)";

  const areaPath = `M${points.split(" ")[0]} L${points} L${
    W - PAD
  },${H} L${PAD},${H} Z`;

  return (
    <svg
      className="quoteWidget-sparkline"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
    >
      <path d={areaPath} fill={fill} />
      <polyline
        points={points}
        fill="none"
        stroke={stroke}
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

const SYMBOL_LABELS: Record<string, { name: string; icon: string }> = {
  BTCUSDT: { name: "Bitcoin", icon: "BTC" },
  ETHUSDT: { name: "Ethereum", icon: "ETH" },
  SOLUSDT: { name: "Solana", icon: "SOL" },
};

function truncateAddress(addr: string | undefined | null) {
  if (!addr) return "";
  if (addr.length <= 14) return addr;
  return `${addr.slice(0, 8)}...${addr.slice(-6)}`;
}

function DepositAddressWidget({
  output,
}: {
  output: McpTools["get_deposit_address"]["output"];
}) {
  const [copied, setCopied] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  if (!output?.address) {
    return (
      <div className="depositWidget">
        <div className="depositWidget-head">
          <div className="depositWidget-icon">↓</div>
          <div>
            <div className="depositWidget-title">Endereço de depósito</div>
            <div className="depositWidget-sub">
              {output?.coin && output?.network
                ? `${output.coin} · ${output.network}`
                : "Indisponível"}
            </div>
          </div>
        </div>
        <div className="depositWidget-hint">
          Não foi possível obter o endereço de depósito. Tente novamente.
        </div>
      </div>
    );
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(output.address);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      console.warn("Falha ao copiar endereço:", err);
    }
  };

  return (
    <div className="depositWidget">
      <div className="depositWidget-head">
        <div className="depositWidget-icon">↓</div>
        <div>
          <div className="depositWidget-title">Endereço de depósito</div>
          <div className="depositWidget-sub">
            {output.coin} · {output.network}
          </div>
        </div>
      </div>

      <div className="depositWidget-qr">
        <QRCodeSVG
          value={output.address}
          size={160}
          level="M"
          marginSize={2}
        />
      </div>

      <button
        type="button"
        className="depositWidget-addressBox"
        onClick={handleCopy}
        title="Copiar endereço completo"
      >
        <span className="depositWidget-addressText">
          {truncateAddress(output.address)}
        </span>
        <span className="depositWidget-copyIcon" aria-hidden="true">
          {copied ? "✓" : "⧉"}
        </span>
      </button>

      <div className="depositWidget-actions">
        <button
          type="button"
          className="depositWidget-btn depositWidget-btn--primary"
          onClick={handleCopy}
        >
          {copied ? "Copiado!" : "Copiar endereço"}
        </button>
        <button
          type="button"
          className="depositWidget-btn depositWidget-btn--ghost"
          onClick={() => setQrOpen(true)}
        >
          Ampliar QR
        </button>
      </div>

      <div className="depositWidget-hint">
        Envie apenas {output.coin} na rede {output.network}. Outros ativos
        podem ser perdidos.
      </div>

      {qrOpen && (
        <div
          className="depositWidget-modal"
          role="dialog"
          aria-modal="true"
          onClick={() => setQrOpen(false)}
        >
          <div
            className="depositWidget-modalBox"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="depositWidget-modalHead">
              <div>
                <div className="depositWidget-title">QR Code</div>
                <div className="depositWidget-sub">
                  {output.coin} · {output.network}
                </div>
              </div>
              <button
                type="button"
                className="depositWidget-close"
                onClick={() => setQrOpen(false)}
                aria-label="Fechar"
              >
                ×
              </button>
            </div>
            <div className="depositWidget-qr depositWidget-qr--large">
              <QRCodeSVG
                value={output.address}
                size={240}
                level="M"
                marginSize={2}
              />
            </div>
            <div className="depositWidget-modalAddress">{output.address}</div>
            <button
              type="button"
              className="depositWidget-btn depositWidget-btn--primary"
              onClick={handleCopy}
            >
              {copied ? "Copiado!" : "Copiar endereço"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

type DepositNetwork =
  McpTools["list_deposit_networks"]["output"]["networks"][number];
type WithdrawNetwork =
  McpTools["list_withdraw_networks"]["output"]["networks"][number];

// Texto da metainformação de cada rede. Depósito mostra o mínimo; saque mostra
// a taxa cobrada e o mínimo — os números que o usuário precisa ver antes de
// escolher a rede.
function depositMeta(net: DepositNetwork, coin: string): string {
  if (net.minDeposit > 0) {
    return `${net.network} · mín. ${net.minDeposit} ${coin}`.trimEnd();
  }
  return net.network;
}

function withdrawMeta(net: WithdrawNetwork, coin: string): string {
  const parts = [net.network];
  if (net.withdrawMin) parts.push(`mín. ${net.withdrawMin} ${coin}`.trimEnd());
  return parts.join(" · ");
}

/**
 * Lista de redes para depósito ou saque. Os dois `list_*_networks` tools
 * compartilham `network`/`name`/`icon`, mas diferem nos campos numéricos
 * (depósito tem `minDeposit`; saque tem `withdrawFee`/`withdrawMin`/…), então o
 * `kind` discrimina o tipo de `networks` e o que é exibido.
 *
 * Ao clicar, envia no chat uma mensagem natural carregando o identificador
 * canônico da rede (o `network`, que é o que `get_deposit_address`/
 * `withdraw_onchain` consomem) e ancorando a ação (depositar/sacar) — o modelo
 * decide os próximos passos a partir do contexto da conversa.
 */
function NetworksWidget(
  props:
    & {
      coin: string;
      sendUserMessage: (content: string) => Promise<unknown>;
      agentIsRunning: boolean;
    }
    & (
      | { kind: "deposit"; networks: DepositNetwork[] }
      | { kind: "withdraw"; networks: WithdrawNetwork[] }
    ),
) {
  const { coin, sendUserMessage, agentIsRunning, kind } = props;
  const [pending, setPending] = useState<string | null>(null);

  const pick = async (net: DepositNetwork | WithdrawNetwork) => {
    if (pending || agentIsRunning) return;
    setPending(net.network);
    try {
      const action = kind === "deposit" ? "depositar" : "sacar";
      await sendUserMessage(
        `Quero ${action} ${coin} na rede ${net.name} (identificador: ${net.network}).`,
      );
    } finally {
      setPending(null);
    }
  };

  if (!props.networks.length) {
    return (
      <div className="networksWidget">
        <div className="networksWidget-empty">
          Nenhuma rede de {kind === "deposit" ? "depósito" : "saque"} disponível
          para {coin || "esta moeda"}.
        </div>
      </div>
    );
  }

  const title = kind === "deposit" ? "Redes para depósito" : "Redes para saque";
  const sub = kind === "deposit"
    ? "Escolha a rede para gerar o endereço de depósito."
    : "Escolha a rede para continuar o saque.";

  return (
    <div className="networksWidget">
      <div className="networksWidget-head">
        <div className="networksWidget-title">
          {title}
          {coin ? ` · ${coin}` : ""}
        </div>
        <div className="networksWidget-sub">{sub}</div>
      </div>
      <div className="networksWidget-list">
        {props.networks.map((net) => {
          const isPending = pending === net.network;
          const meta = props.kind === "deposit"
            ? depositMeta(net as DepositNetwork, coin)
            : withdrawMeta(net as WithdrawNetwork, coin);
          const fee = props.kind === "withdraw"
            ? (net as WithdrawNetwork).withdrawFee
            : null;
          return (
            <button
              key={net.network}
              type="button"
              className="networksWidget-item"
              disabled={!!pending || agentIsRunning}
              onClick={() => pick(net)}
            >
              <div className="networksWidget-itemIcon">
                {net.icon
                  ? <img src={net.icon} alt={net.name} />
                  : net.network.slice(0, 3)}
              </div>
              <div className="networksWidget-itemBody">
                <div className="networksWidget-itemName">{net.name}</div>
                <div className="networksWidget-itemMeta">{meta}</div>
                {fee
                  ? (
                    <div className="networksWidget-itemFee">
                      Taxa de saque: {fee} {coin}
                    </div>
                  )
                  : null}
              </div>
              <span className="networksWidget-itemChevron" aria-hidden="true">
                {isPending ? "…" : "→"}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Local types mirroring the backend `UpdateUserSettingsTool` schema in
 * `wedofi-api/src/infrastructure/http/mcp/mcp_tools_openapi.ts`. These exist
 * because Orval needs to be re-run (`npm run api:generate`) to refresh
 * `McpTools["update_user_settings"]`. Once that's done, these can be deleted
 * and the renderer can adopt the generated types like every other tool.
 */
type UpdateUserSettingsLanguage = "pt-BR" | "en-US" | "es-ES";
type UpdateUserSettingsCurrency = "BRL" | "USD" | "EUR" | "GBP" | "PYG";

type UpdateUserSettingsInput = {
  language?: UpdateUserSettingsLanguage;
  defaultCurrency?: UpdateUserSettingsCurrency;
  timeZone?: string;
  dateFormat?: string;
  emailNotifications?: boolean;
  marketingEmails?: boolean;
};

type UpdateUserSettingsOutput = {
  userId: string;
  language: UpdateUserSettingsLanguage;
  defaultCurrency: UpdateUserSettingsCurrency;
  timeZone: string;
  dateFormat: string;
  emailNotifications: boolean;
  marketingEmails: boolean;
  updatedFields: string[];
  updatedAt: string;
};

function getUpdateUserSettingsOutput(
  tool: ChatTool,
): UpdateUserSettingsOutput | undefined {
  const raw = tool.tool_end?.output;

  if (!raw || typeof raw !== "object") {
    return undefined;
  }

  const output = "structured_content" in raw
    ? (raw as { structured_content?: unknown }).structured_content
    : raw;

  if (!output || typeof output !== "object") {
    return undefined;
  }

  return output as UpdateUserSettingsOutput;
}

function getUpdateUserSettingsInput(
  tool: ChatTool,
): UpdateUserSettingsInput | undefined {
  const raw = tool.tool_start?.input;

  if (!raw || typeof raw !== "object") {
    return undefined;
  }

  return raw as UpdateUserSettingsInput;
}

const SETTINGS_FIELD_LABEL: Record<keyof UpdateUserSettingsOutput, string> = {
  userId: "ID do usuário",
  language: "Idioma",
  defaultCurrency: "Moeda padrão",
  timeZone: "Fuso horário",
  dateFormat: "Formato de data",
  emailNotifications: "Notificações por email",
  marketingEmails: "Emails de marketing",
  updatedFields: "Campos atualizados",
  updatedAt: "Atualizado em",
};

const LANGUAGE_LABEL: Record<UpdateUserSettingsLanguage, string> = {
  "pt-BR": "Português (pt-BR)",
  "en-US": "English (en-US)",
  "es-ES": "Español (es-ES)",
};

const CURRENCY_LABEL: Record<UpdateUserSettingsCurrency, string> = {
  BRL: "Real brasileiro (BRL)",
  USD: "Dólar americano (USD)",
  EUR: "Euro (EUR)",
  GBP: "Libra esterlina (GBP)",
  PYG: "Guarani paraguaio (PYG)",
};

function formatSettingsValue(
  field: string,
  value: unknown,
): string {
  if (value === null || value === undefined) {
    return "—";
  }

  if (field === "language" && typeof value === "string" && value in LANGUAGE_LABEL) {
    return LANGUAGE_LABEL[value as UpdateUserSettingsLanguage];
  }

  if (
    field === "defaultCurrency" && typeof value === "string" &&
    value in CURRENCY_LABEL
  ) {
    return CURRENCY_LABEL[value as UpdateUserSettingsCurrency];
  }

  if (typeof value === "boolean") {
    return value ? "Ativado" : "Desativado";
  }

  return String(value);
}

function UserSettingsUpdatedWidget({
  input,
  output,
}: {
  input: UpdateUserSettingsInput | undefined;
  output: UpdateUserSettingsOutput;
}) {
  // Prefer the input keys (what the LLM actually asked to change) over the
  // backend's `updatedFields` mirror — they should match, but the input is
  // the source of truth for "what the user requested in this turn".
  const requestedFields = input
    ? (Object.keys(input).filter(
      (k) => input[k as keyof UpdateUserSettingsInput] !== undefined,
    ) as Array<keyof UpdateUserSettingsInput>)
    : (output.updatedFields ?? []) as Array<keyof UpdateUserSettingsInput>;

  const changes = requestedFields
    .map((field) => {
      const newValue = output[field as keyof UpdateUserSettingsOutput];
      return {
        field,
        label: SETTINGS_FIELD_LABEL[field as keyof UpdateUserSettingsOutput] ??
          field,
        value: formatSettingsValue(field, newValue),
      };
    })
    .filter((c) => c.value !== "—");

  const updatedAt = output.updatedAt
    ? new Date(output.updatedAt).toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    })
    : null;

  return (
    <div className="settingsWidget">
      <div className="settingsWidget-head">
        <div className="settingsWidget-icon" aria-hidden="true">⚙</div>
        <div className="settingsWidget-headText">
          <div className="settingsWidget-title">
            Preferências atualizadas
          </div>
          <div className="settingsWidget-sub">
            {changes.length > 0
              ? `${changes.length} ${
                changes.length === 1 ? "campo alterado" : "campos alterados"
              }`
              : "Nenhum campo alterado"}
            {updatedAt ? ` · ${updatedAt}` : ""}
          </div>
        </div>
        <span className="settingsWidget-badge">Salvo</span>
      </div>

      {changes.length > 0
        ? (
          <ul className="settingsWidget-list">
            {changes.map((c) => (
              <li className="settingsWidget-item" key={c.field}>
                <span className="settingsWidget-itemLabel">{c.label}</span>
                <span className="settingsWidget-itemValue">{c.value}</span>
              </li>
            ))}
          </ul>
        )
        : null}

      {(requestedFields as string[]).includes("language") ||
          (requestedFields as string[]).includes("defaultCurrency")
        ? (
          <div className="settingsWidget-hint">
            A nova preferência se aplica a partir da próxima mensagem.
          </div>
        )
        : null}
    </div>
  );
}

/**
 * Local types mirroring the backend `ListOrdersTool` schema in
 * `wedofi-api/src/infrastructure/http/mcp/mcp_tools_openapi.ts`. They exist
 * because Orval needs to be re-run (`npm run api:generate`, pointed at a
 * backend that already has the `list_orders` tool) to refresh
 * `McpTools["list_orders"]`. Once that's done, these can be deleted and the
 * renderer can adopt `getToolOutput<"list_orders">` like every other tool.
 */
type ListOrdersSide = "BUY" | "SELL";
type ListOrdersCategory = "SPOT" | "LINEAR";
type ListOrdersProduct = "MARKET" | "LIMIT" | "SWAP" | "FUTURE";
type ListOrdersStatus =
  | "NEW"
  | "PARTIALLY_FILLED"
  | "FILLED"
  | "CANCELLED"
  | "REJECTED";

type ListOrdersLinear = {
  positionSide: "LONG" | "SHORT";
  leverage?: string;
  marginMode?: string;
  initialMargin?: string;
  entryPrice?: string;
  liqPrice?: string;
  markPrice?: string;
  unrealisedPnl?: string;
  realizedPnl?: string;
  closedQty: string;
  remainingQty: string;
  closedAt: string | null;
  takeProfit: string | null;
  stopLoss: string | null;
  reduceOnly: boolean;
};

type ListOrdersItem = {
  orderId: string;
  historyId: string;
  symbol: string;
  baseCoin: string;
  quoteCoin: string;
  side: ListOrdersSide;
  orderType: "MARKET" | "LIMIT";
  category: ListOrdersCategory;
  product: ListOrdersProduct;
  qty: string;
  price: string | null;
  orderStatus: ListOrdersStatus;
  cumExecQty: string;
  leavesQty: string;
  avgPrice: string | null;
  createdAt: string;
  linear?: ListOrdersLinear;
};

type ListOrdersInput = {
  openOnly?: boolean;
  category?: ListOrdersCategory;
  product?: ListOrdersProduct;
  symbol?: string;
};

type ListOrdersOutput = {
  orders: ListOrdersItem[];
};

function getListOrdersOutput(tool: ChatTool): ListOrdersOutput | undefined {
  const raw = tool.tool_end?.output;
  if (!raw || typeof raw !== "object") return undefined;

  const output = "structured_content" in raw
    ? (raw as { structured_content?: unknown }).structured_content
    : raw;

  if (!output || typeof output !== "object" || !("orders" in output)) {
    return undefined;
  }
  return output as ListOrdersOutput;
}

function getListOrdersInput(tool: ChatTool): ListOrdersInput | undefined {
  const raw = tool.tool_start?.input;
  if (!raw || typeof raw !== "object") return undefined;
  return raw as ListOrdersInput;
}

const ORDER_STATUS_LABEL: Record<ListOrdersStatus, string> = {
  NEW: "Aberta",
  PARTIALLY_FILLED: "Parcial",
  FILLED: "Executada",
  CANCELLED: "Cancelada",
  REJECTED: "Rejeitada",
};

/**
 * Rótulo do tipo de execução da ordem. `orderType` (MARKET/LIMIT) é o
 * discriminador uniforme entre spot/futuros/swap — diferente do `product`, que
 * agrupa o spot market/limit num único "Spot". É o que dá ao usuário a noção de
 * "essa ordem é a limite ou a mercado".
 */
const ORDER_TYPE_LABEL: Record<ListOrdersItem["orderType"], string> = {
  MARKET: "Mercado",
  LIMIT: "Limite",
};

/**
 * Rótulo de status para exibição. Uma posição linear aberta aparece como
 * "Aberta": o pedido de entrada foi executado (FILLED), mas a posição segue
 * aberta e ao vivo — mostrá-la como "Executada" confundiria com algo encerrado.
 * Uma posição já fechada aparece como "Fechada" (mais claro que "Executada").
 */
function statusLabelFor(item: ListOrdersItem): string {
  if (isOpenLinearPosition(item)) return "Aberta";
  if (isClosedLinearPosition(item)) return "Fechada";
  return ORDER_STATUS_LABEL[item.orderStatus] ?? item.orderStatus;
}

function formatSignedUSD(value: number): string {
  const abs = Math.abs(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${value >= 0 ? "+" : "-"}$${abs}`;
}

function formatMaybePrice(value?: string | null): string {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  if (!Number.isFinite(num)) return value;
  return `$${formatPrice(value)}`;
}

/**
 * Polling compartilhado de `GET /trading/orders`. Por mais cards de
 * `list_orders` que estejam montados ao mesmo tempo, há um único intervalo e um
 * único request por ciclo — o resultado é transmitido a todos os assinantes, e
 * cada card mescla no seu próprio estado. Mesmo mecanismo da rota, que enriquece
 * a posição via Bybit a cada request.
 */
type OrdersListener = (live: ListOrdersItem[]) => void;

const ordersPoller = (() => {
  const POLL_MS = 7000;
  const listeners = new Set<OrdersListener>();
  let timer: number | null = null;
  let inFlight = false;

  const tick = async () => {
    if (inFlight) return; // coalesce: nunca dois requests em voo simultâneos
    inFlight = true;
    try {
      const response = await getTradingOrders();
      if (response.status !== 200 || !("data" in response.data)) return;
      const live = response.data.data as unknown as ListOrdersItem[];
      listeners.forEach((notify) => notify(live));
    } catch (err) {
      console.warn("Falha ao atualizar ordens (polling):", err);
    } finally {
      inFlight = false;
    }
  };

  return {
    subscribe(listener: OrdersListener): () => void {
      const isFirst = listeners.size === 0;
      listeners.add(listener);
      if (timer === null) {
        timer = window.setInterval(tick, POLL_MS);
      }
      // Atualização imediata só ao iniciar o ciclo (1º assinante). Assinantes
      // seguintes pegam carona no próximo tick — garante um request por ciclo.
      if (isFirst) void tick();
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0 && timer !== null) {
          window.clearInterval(timer);
          timer = null;
        }
      };
    },
  };
})();

function OpenOrdersWidget({
  output,
  input,
  agentIsRunning,
  sendUserMessage,
}: {
  output: ListOrdersOutput;
  input: ListOrdersInput | undefined;
  agentIsRunning: boolean;
  sendUserMessage: (content: string) => Promise<unknown>;
}) {
  const [orders, setOrders] = useState<ListOrdersItem[]>(output.orders);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [pendingAction, setPendingAction] = useState<string | null>(null);

  // Fechar (posição futura aberta) / Cancelar (ordem pendente): envia uma
  // mensagem no chat e deixa o agente disparar o tool com HITL. Mesmo padrão do
  // NetworksWidget. O id enviado é o `orderId` público (bybitOrderId) que o
  // agente repassa verbatim ao close_position/cancel_order.
  const runAction = async (order: ListOrdersItem, kind: "close" | "cancel") => {
    if (pendingAction || agentIsRunning) return;
    setPendingAction(order.orderId);
    try {
      await sendUserMessage(
        kind === "close"
          ? `Fechar ordem com id ${order.orderId}`
          : `Cancelar ordem com id ${order.orderId}`,
      );
    } finally {
      setPendingAction(null);
    }
  };

  // Reseta quando o snapshot do tool muda (nova chamada da ferramenta).
  useEffect(() => {
    setOrders(output.orders);
    setUpdatedAt(null);
  }, [output.orders]);

  // Para o polling quando o usuário envia uma nova mensagem (o agente volta a
  // rodar): detecta a borda de subida de `agentIsRunning` após a montagem e
  // congela os valores deste card.
  const [stopped, setStopped] = useState(false);
  const wasRunningRef = useRef(agentIsRunning);
  useEffect(() => {
    if (agentIsRunning && !wasRunningRef.current) {
      setStopped(true);
    }
    wasRunningRef.current = agentIsRunning;
  }, [agentIsRunning]);

  // Polling: só vale a pena enquanto houver posição linear aberta (a única com
  // unrealisedPnl vivo) e o card não tiver sido congelado. Assina o poller
  // compartilhado (um request por ciclo p/ todos os cards) e mescla os campos
  // live (markPrice, unrealisedPnl, liq, entry, status) por orderId.
  const hasOpenPositions = orders.some(isOpenLinearPosition);
  const isLive = hasOpenPositions && !stopped;

  useEffect(() => {
    if (!isLive) return;

    return ordersPoller.subscribe((live) => {
      const byId = new Map(live.map((o) => [o.orderId, o]));
      setOrders((prev) =>
        prev.map((o) => {
          const fresh = byId.get(o.orderId);
          if (!fresh) return o;
          return {
            ...o,
            orderStatus: fresh.orderStatus,
            avgPrice: fresh.avgPrice ?? o.avgPrice,
            cumExecQty: fresh.cumExecQty ?? o.cumExecQty,
            leavesQty: fresh.leavesQty ?? o.leavesQty,
            linear: o.linear
              ? { ...o.linear, ...(fresh.linear ?? {}) }
              : fresh.linear,
          };
        })
      );
      setUpdatedAt(new Date());
    });
  }, [isLive]);

  // O modo (abertas vs todas) é decidido pelo agente via input (a partir do
  // prompt do usuário) — única fonte de verdade. Default true espelha o backend.
  const openOnly = input?.openOnly ?? true;

  if (!orders.length) {
    return (
      <div className="ordersWidget">
        <div className="ordersWidget-empty">
          {openOnly
            ? "Nenhuma ordem aberta no momento."
            : "Nenhuma ordem encontrada."}
        </div>
      </div>
    );
  }

  // Soma do P&L não realizado das posições abertas (visão rápida).
  const totalPnl = orders.reduce((acc, o) => {
    if (!isOpenLinearPosition(o)) return acc;
    const n = Number(o.linear?.unrealisedPnl ?? "");
    return Number.isFinite(n) ? acc + n : acc;
  }, 0);
  const hasPnl = orders.some(
    (o) => isOpenLinearPosition(o) && o.linear?.unrealisedPnl !== undefined,
  );

  return (
    <div className="ordersWidget">
      <div className="ordersWidget-head">
        <div className="ordersWidget-icon" aria-hidden="true">📈</div>
        <div className="ordersWidget-headText">
          <div className="ordersWidget-title">
            {openOnly ? "Ordens abertas" : "Minhas ordens"}
          </div>
          <div className="ordersWidget-sub">
            {orders.length}{" "}
            {orders.length === 1 ? "ordem" : "ordens"}
            {hasOpenPositions && (
              isLive
                ? (
                  <span className="ordersWidget-live">
                    <span className="ordersWidget-liveDot" /> ao vivo
                    {updatedAt
                      ? ` · ${updatedAt.toLocaleTimeString("pt-BR", {
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      })}`
                      : ""}
                  </span>
                )
                : (
                  <span className="ordersWidget-live ordersWidget-live--paused">
                    pausado
                  </span>
                )
            )}
          </div>
        </div>
        {hasPnl && (
          <span
            className={`ordersWidget-totalPnl ${
              totalPnl >= 0
                ? "ordersWidget-totalPnl--up"
                : "ordersWidget-totalPnl--down"
            }`}
          >
            {formatSignedUSD(totalPnl)}
          </span>
        )}
      </div>

      <ul className="ordersWidget-list">
        {orders.map((o) => {
          const isFuture = o.product === "FUTURE" && !!o.linear;
          const isOpenPosition = isOpenLinearPosition(o);
          // Posição futura aberta → fechar; ordem pendente → cancelar; demais
          // (executada, cancelada, rejeitada, já fechada) → sem ação.
          const action: "close" | "cancel" | null = isOpenPosition
            ? "close"
            : isCancellableOrder(o)
            ? "cancel"
            : null;
          const pnlStr = o.linear?.unrealisedPnl;
          const pnlNum = pnlStr !== undefined ? Number(pnlStr) : null;
          const hasPnl = pnlNum !== null && Number.isFinite(pnlNum);
          const pnlUp = pnlNum !== null && pnlNum >= 0;
          const directionLabel = isFuture
            ? o.linear!.positionSide
            : o.side;
          const directionUp = isFuture
            ? o.linear!.positionSide === "LONG"
            : o.side === "BUY";

          return (
            <li className="ordersWidget-item" key={o.orderId}>
              <div className="ordersWidget-itemTop">
                <div className="ordersWidget-itemSymbol">
                  <span className="ordersWidget-symbolText">{o.symbol}</span>
                  <span
                    className={`ordersWidget-side ${
                      directionUp
                        ? "ordersWidget-side--up"
                        : "ordersWidget-side--down"
                    }`}
                  >
                    {directionLabel}
                    {isFuture && o.linear?.leverage
                      ? ` · ${o.linear.leverage}x`
                      : ""}
                  </span>
                  <span className="ordersWidget-tag">
                    {o.product === "FUTURE"
                      ? "Futuros"
                      : o.product === "SWAP"
                      ? "Swap"
                      : "Spot"}
                  </span>
                  <span className="ordersWidget-tag ordersWidget-tag--type">
                    {ORDER_TYPE_LABEL[o.orderType]}
                  </span>
                </div>

                {isOpenPosition
                  ? (
                    <span
                      className={`ordersWidget-pnl ${
                        !hasPnl
                          ? "ordersWidget-pnl--pending"
                          : pnlUp
                          ? "ordersWidget-pnl--up"
                          : "ordersWidget-pnl--down"
                      }`}
                    >
                      {hasPnl
                        ? `${pnlUp ? "▲" : "▼"} ${formatSignedUSD(pnlNum!)}`
                        : "Aberta · —"}
                    </span>
                  )
                  : (
                    <span
                      className={`ordersWidget-status ordersWidget-status--${o.orderStatus}`}
                    >
                      {statusLabelFor(o)}
                    </span>
                  )}
              </div>

              <div className="ordersWidget-itemMeta">
                <div className="ordersWidget-metaCell">
                  <span className="ordersWidget-metaLabel">Qtd</span>
                  <span className="ordersWidget-metaValue">
                    {o.qty} {o.baseCoin}
                  </span>
                </div>
                {isFuture
                  ? (
                    <>
                      <div className="ordersWidget-metaCell">
                        <span className="ordersWidget-metaLabel">Entrada</span>
                        <span className="ordersWidget-metaValue">
                          {formatMaybePrice(o.linear?.entryPrice)}
                        </span>
                      </div>
                      <div className="ordersWidget-metaCell">
                        <span className="ordersWidget-metaLabel">Atual</span>
                        <span className="ordersWidget-metaValue">
                          {formatMaybePrice(o.linear?.markPrice)}
                        </span>
                      </div>
                      <div className="ordersWidget-metaCell">
                        <span className="ordersWidget-metaLabel">Liq.</span>
                        <span className="ordersWidget-metaValue">
                          {formatMaybePrice(o.linear?.liqPrice)}
                        </span>
                      </div>
                    </>
                  )
                  : (
                    <>
                      <div className="ordersWidget-metaCell">
                        <span className="ordersWidget-metaLabel">Preço</span>
                        <span className="ordersWidget-metaValue">
                          {o.orderType === "MARKET" && o.price === null
                            ? "Mercado"
                            : formatMaybePrice(o.price)}
                        </span>
                      </div>
                      <div className="ordersWidget-metaCell">
                        <span className="ordersWidget-metaLabel">Status</span>
                        <span className="ordersWidget-metaValue">
                          {statusLabelFor(o)}
                        </span>
                      </div>
                    </>
                  )}
              </div>

              {action && (
                <div className="ordersWidget-actions">
                  <button
                    type="button"
                    className={`ordersWidget-actionBtn ordersWidget-actionBtn--${action}`}
                    disabled={!!pendingAction || agentIsRunning}
                    onClick={() => runAction(o, action)}
                  >
                    {pendingAction === o.orderId
                      ? "Enviando…"
                      : action === "close"
                      ? "Fechar posição"
                      : "Cancelar ordem"}
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Local types mirroring the backend `WalletHistoryTool` schema in
 * `wedofi-api/src/infrastructure/http/mcp/mcp_tools_openapi.ts`. They exist
 * because Orval needs to be re-run (`npm run api:generate`, pointed at a
 * backend that already has the `wallet_history` tool) to refresh
 * `McpTools["wallet_history"]`. Once that's done, these can be deleted and the
 * renderer can adopt `getToolOutput<"wallet_history">` like every other tool.
 */
type WalletHistorySide = "BUY" | "SELL" | "DEPOSIT" | "WITHDRAW";
type WalletHistoryProduct =
  | "ON_RAMP"
  | "OFF_RAMP"
  | "TRADE_SPOT"
  | "TRADE_SWAP"
  | "TRADE_LINEAR"
  | "DEPOSIT_ONCHAIN"
  | "WITHDRAW_ONCHAIN";
type WalletHistoryStatus =
  | "CREATED"
  | "AWAITING_PAYMENT"
  | "PROCESSING"
  | "CONFIRMED"
  | "FAILED"
  | "EXPIRED"
  | "CANCELLED";

type WalletHistoryMove = {
  id: string;
  internalId: string;
  externalId: string;
  product: WalletHistoryProduct;
  side: WalletHistorySide;
  coin: string;
  amount: string;
  fiatAmount: string | null;
  fiatCurrency: string | null;
  fee: string | null;
  feeCoin: string | null;
  avgPrice: string | null;
  status: WalletHistoryStatus;
  rampPaymentMethod: string | null;
  network: string | null;
  txHash: string | null;
  createdAt: string;
  updatedAt: string;
  confirmedAt: string | null;
  details: Record<string, unknown>;
};

type WalletHistoryInput = {
  side?: WalletHistorySide;
  product?: WalletHistoryProduct;
  coin?: string;
  period?: "7d" | "30d" | "90d" | "all";
  cursor?: string;
};

type WalletHistoryOutput = {
  items: WalletHistoryMove[];
  nextCursor: string | null;
  hasMoreAfter: boolean;
};

function getWalletHistoryOutput(tool: ChatTool): WalletHistoryOutput | undefined {
  const raw = tool.tool_end?.output;
  if (!raw || typeof raw !== "object") return undefined;

  const output = "structured_content" in raw
    ? (raw as { structured_content?: unknown }).structured_content
    : raw;

  if (!output || typeof output !== "object" || !("items" in output)) {
    return undefined;
  }
  return output as WalletHistoryOutput;
}

function getWalletHistoryInput(tool: ChatTool): WalletHistoryInput | undefined {
  const raw = tool.tool_start?.input;
  if (!raw || typeof raw !== "object") return undefined;
  return raw as WalletHistoryInput;
}

const WALLET_PRODUCT_LABEL: Record<WalletHistoryProduct, string> = {
  ON_RAMP: "Compra",
  OFF_RAMP: "Venda",
  TRADE_SPOT: "Spot",
  TRADE_SWAP: "Swap",
  TRADE_LINEAR: "Futuros",
  DEPOSIT_ONCHAIN: "Depósito",
  WITHDRAW_ONCHAIN: "Saque",
};

const WALLET_STATUS_LABEL: Record<WalletHistoryStatus, string> = {
  CREATED: "Criado",
  AWAITING_PAYMENT: "Aguardando pagamento",
  PROCESSING: "Processando",
  CONFIRMED: "Confirmado",
  FAILED: "Falhou",
  EXPIRED: "Expirado",
  CANCELLED: "Cancelado",
};

// Entrada (income) vs saída (outcome). Espelha a equação do produto:
// depósito/compra entram na carteira; saque/venda saem dela.
function isIncomeMove(side: WalletHistorySide): boolean {
  return side === "DEPOSIT" || side === "BUY";
}

const WALLET_ORDER_TYPE_LABEL: Record<string, string> = {
  MARKET: "Mercado",
  LIMIT: "Limite",
};

// Para ordens de trading (Spot/Futuros), extrai o tipo de execução
// (Mercado/Limite) de `details.orderType`. Null para os demais produtos.
function getMoveOrderTypeLabel(move: WalletHistoryMove): string | null {
  if (move.product !== "TRADE_SPOT" && move.product !== "TRADE_LINEAR") return null;
  const orderType = move.details?.orderType;
  if (typeof orderType !== "string" || !orderType) return null;
  const label = WALLET_ORDER_TYPE_LABEL[orderType] ?? orderType;
  const limitPrice = move.details?.limitPrice;
  if (orderType === "LIMIT" && typeof limitPrice === "string" && limitPrice) {
    return `${label} @ ${limitPrice}`;
  }
  return label;
}

function formatMoveDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Título do card a partir do filtro que o agente aplicou (input). Sem filtro de
// direção, mostra o rótulo genérico "Movimentações".
function walletHistoryTitle(input: WalletHistoryInput | undefined): string {
  switch (input?.side) {
    case "DEPOSIT":
      return "Depósitos";
    case "WITHDRAW":
      return "Saques";
    case "BUY":
      return "Entradas";
    case "SELL":
      return "Saídas";
    default:
      return "Movimentações";
  }
}

function WalletHistoryWidget({
  output,
  input,
  agentIsRunning,
  sendUserMessage,
}: {
  output: WalletHistoryOutput;
  input: WalletHistoryInput | undefined;
  agentIsRunning: boolean;
  sendUserMessage: (content: string) => Promise<unknown>;
}) {
  const [loadingMore, setLoadingMore] = useState(false);
  const title = walletHistoryTitle(input);

  const loadMore = async () => {
    if (loadingMore || agentIsRunning) return;
    setLoadingMore(true);
    try {
      // O agente já tem o `nextCursor` deste resultado no contexto e a
      // descrição da tool o instrui a repassá-lo como `cursor`. Mesmo padrão
      // de "enviar mensagem no chat" usado pelos demais widgets.
      await sendUserMessage("Ver a próxima página das minhas movimentações.");
    } finally {
      setLoadingMore(false);
    }
  };

  if (!output.items.length) {
    return (
      <div className="walletHistoryWidget">
        <div className="walletHistoryWidget-empty">
          Nenhuma movimentação encontrada.
        </div>
      </div>
    );
  }

  return (
    <div className="walletHistoryWidget">
      <div className="walletHistoryWidget-head">
        <div className="walletHistoryWidget-icon" aria-hidden="true">📒</div>
        <div className="walletHistoryWidget-headText">
          <div className="walletHistoryWidget-title">{title}</div>
          <div className="walletHistoryWidget-sub">
            {output.items.length}{" "}
            {output.items.length === 1 ? "movimentação" : "movimentações"}
          </div>
        </div>
      </div>

      <ul className="walletHistoryWidget-list">
        {output.items.map((move) => {
          const income = isIncomeMove(move.side);
          return (
            <li className="walletHistoryWidget-item" key={move.id}>
              <div className="walletHistoryWidget-itemTop">
                <div className="walletHistoryWidget-itemLeft">
                  <span
                    className={`walletHistoryWidget-dir ${
                      income
                        ? "walletHistoryWidget-dir--in"
                        : "walletHistoryWidget-dir--out"
                    }`}
                    aria-hidden="true"
                  >
                    {income ? "↓" : "↑"}
                  </span>
                  <div className="walletHistoryWidget-itemInfo">
                    <span className="walletHistoryWidget-product">
                      {WALLET_PRODUCT_LABEL[move.product] ?? move.product}
                    </span>
                    <span className="walletHistoryWidget-date">
                      {formatMoveDate(move.createdAt)}
                    </span>
                  </div>
                </div>
                <div className="walletHistoryWidget-itemRight">
                  <span
                    className={`walletHistoryWidget-amount ${
                      income
                        ? "walletHistoryWidget-amount--in"
                        : "walletHistoryWidget-amount--out"
                    }`}
                  >
                    {income ? "+" : "-"}
                    {move.amount} {move.coin}
                  </span>
                  {move.fiatAmount && (
                    <span className="walletHistoryWidget-fiat">
                      {move.fiatAmount} {move.fiatCurrency ?? ""}
                    </span>
                  )}
                </div>
              </div>
              <div className="walletHistoryWidget-itemMeta">
                <span
                  className={`walletHistoryWidget-status walletHistoryWidget-status--${move.status}`}
                >
                  {WALLET_STATUS_LABEL[move.status] ?? move.status}
                </span>
                {getMoveOrderTypeLabel(move) && (
                  <span className="walletHistoryWidget-tag walletHistoryWidget-tag--ordertype">
                    {getMoveOrderTypeLabel(move)}
                  </span>
                )}
                {move.network && (
                  <span className="walletHistoryWidget-tag">{move.network}</span>
                )}
                {move.rampPaymentMethod && (
                  <span className="walletHistoryWidget-tag">
                    {move.rampPaymentMethod}
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {output.hasMoreAfter && (
        <div className="walletHistoryWidget-actions">
          <button
            type="button"
            className="walletHistoryWidget-moreBtn"
            disabled={loadingMore || agentIsRunning}
            onClick={loadMore}
          >
            {loadingMore ? "Carregando…" : "Carregar mais"}
          </button>
        </div>
      )}
    </div>
  );
}

// A tool `list_dcai_plans` pode ainda não estar no `McpTools` gerado (o orval
// roda contra um backend cujo spec já tenha a tool). Enquanto isso, tipamos
// localmente e desembrulhamos `structured_content` — mesmo padrão temporário do
// `update_user_settings`.
type DcaiPlanItem = {
  id: string;
  name: string;
  status: string;
  symbol: string;
  baseCoin: string;
  quoteCoin: string;
  contributionAmount: string;
  frequency: string;
  nextRunAt: string | null;
  totalCyclesCount: number;
  completedCyclesCount: number;
  skippedCyclesCount: number;
  accumulatedBaseAmount: string;
  accumulatedValueUsdt: string | null;
};

type ListDcaiPlansOutput = {
  plans: DcaiPlanItem[];
};

function getListDcaiPlansOutput(
  tool: ChatTool,
): ListDcaiPlansOutput | undefined {
  const raw = tool.tool_end?.output;
  if (!raw || typeof raw !== "object") return undefined;
  const output = "structured_content" in raw
    ? (raw as { structured_content?: unknown }).structured_content
    : raw;
  if (!output || typeof output !== "object") return undefined;
  return output as ListDcaiPlansOutput;
}

const DCAI_PLAN_FREQUENCY_LABEL: Record<string, string> = {
  WEEKLY: "Semanal",
  MONTHLY: "Mensal",
  BIWEEKLY: "Quinzenal",
  QUARTERLY: "Trimestral",
  HALF_HOURLY: "A cada 30 min",
  MINUTELY: "A cada minuto",
};

const DCAI_PLAN_STATUS_LABEL: Record<string, string> = {
  ACTIVE: "Ativo",
  DISABLING: "Encerrando",
  DISABLED: "Encerrado",
  DRAFT: "Rascunho",
};

function DcaiPlansWidget({ plans }: { plans: DcaiPlanItem[] }) {
  const money = (s: string) => {
    const n = parseFloat(s);
    return Number.isFinite(n)
      ? n.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      : s;
  };
  // Quantidade de cripto: mais casas que `money` (0.008 BTC não pode virar 0,01).
  const qty = (s: string) => {
    const n = parseFloat(s);
    return Number.isFinite(n)
      ? n.toLocaleString("pt-BR", { maximumFractionDigits: 8 })
      : s;
  };
  const dateFmt = (s: string | null) => {
    if (!s) return "—";
    const d = new Date(s);
    return Number.isNaN(d.getTime())
      ? "—"
      : d.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
  };

  if (plans.length === 0) {
    return (
      <div className="dcaiWidget">
        <div className="dcaiWidget-head">
          <div className="dcaiWidget-icon">🤖</div>
          <div>
            <div className="dcaiWidget-title">DCAi</div>
            <div className="dcaiWidget-subtitle">Meus planos</div>
          </div>
        </div>
        <div className="dcaiWidget-empty">Você não tem planos de DCAi.</div>
      </div>
    );
  }

  return (
    <div className="dcaiWidget">
      <div className="dcaiWidget-head">
        <div className="dcaiWidget-icon">🤖</div>
        <div>
          <div className="dcaiWidget-title">DCAi</div>
          <div className="dcaiWidget-subtitle">
            {plans.length === 1 ? "1 plano" : `${plans.length} planos`}
          </div>
        </div>
      </div>

      <div className="dcaiWidget-coins">
        {plans.map((plan) => (
          <div className="dcaiWidget-coin" key={plan.id}>
            <div className="dcaiWidget-coinHead">
              <span className="dcaiWidget-coinIcon">
                {(plan.baseCoin || "?").slice(0, 3)}
              </span>
              <span className="dcaiWidget-coinName">
                {plan.name || plan.symbol || plan.baseCoin}
              </span>
              <span className="dcaiPlansWidget-status">
                {DCAI_PLAN_STATUS_LABEL[plan.status] ?? plan.status}
              </span>
            </div>
            <div className="dcaiWidget-coinRow">
              <span className="dcaiWidget-coinLabel">Aporte por ciclo</span>
              <span className="dcaiWidget-coinValue">
                {money(plan.contributionAmount)} {plan.quoteCoin}
              </span>
            </div>
            <div className="dcaiWidget-coinRow">
              <span className="dcaiWidget-coinLabel">Frequência</span>
              <span className="dcaiWidget-coinValue">
                {DCAI_PLAN_FREQUENCY_LABEL[plan.frequency] ?? plan.frequency}
              </span>
            </div>
            {parseFloat(plan.accumulatedBaseAmount) > 0 && (
              <div className="dcaiWidget-coinRow">
                <span className="dcaiWidget-coinLabel">Acumulado</span>
                <span className="dcaiWidget-coinValue">
                  {qty(plan.accumulatedBaseAmount)} {plan.baseCoin}
                  {plan.accumulatedValueUsdt
                    ? ` (≈ ${money(plan.accumulatedValueUsdt)} USDT)`
                    : ""}
                </span>
              </div>
            )}
            <div className="dcaiWidget-coinRow">
              <span className="dcaiWidget-coinLabel">Próxima execução</span>
              <span className="dcaiWidget-coinValue">
                {dateFmt(plan.nextRunAt)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// As tools de scheduled actions podem ainda não estar no `McpTools` gerado (o
// orval roda contra um backend cujo spec já tenha a tool). Enquanto isso,
// tipamos localmente e desembrulhamos `structured_content` — mesmo padrão
// temporário do `list_dcai_plans`.
type ScheduledActionItem = {
  id: string;
  type: string;
  status: string;
  symbol: string;
  baseCoin: string;
  quoteCoin: string;
  targetPrice: string;
  direction: string;
  currentPrice: string | null;
  recurring: boolean;
  armed: boolean;
  expiresAt: string | null;
  lastTriggeredAt: string | null;
  failureReason: string | null;
  createdAt: string;
};

type ListScheduledActionsOutput = {
  actions: ScheduledActionItem[];
};

function unwrapStructuredContent(tool: ChatTool): unknown {
  const raw = tool.tool_end?.output;
  if (!raw || typeof raw !== "object") return undefined;
  const output = "structured_content" in raw
    ? (raw as { structured_content?: unknown }).structured_content
    : raw;
  if (!output || typeof output !== "object") return undefined;
  return output;
}

function getListScheduledActionsOutput(
  tool: ChatTool,
): ListScheduledActionsOutput | undefined {
  return unwrapStructuredContent(tool) as ListScheduledActionsOutput | undefined;
}

// Recibo das tools HITL create/delete_scheduled_action: só interessa o caso
// executado (rejeição/erro já são narrados pelo card de aprovação).
type ScheduledActionToolResult = {
  status?: string;
  action?: ScheduledActionItem;
};

function getScheduledActionToolResult(
  tool: ChatTool,
): ScheduledActionToolResult | undefined {
  return unwrapStructuredContent(tool) as ScheduledActionToolResult | undefined;
}

const SCHEDULED_ACTION_TYPE_LABEL: Record<string, string> = {
  PRICE_ALERT: "Alerta de preço",
  BUY: "Compra",
  SELL: "Venda",
};

const SCHEDULED_ACTION_STATUS_LABEL: Record<string, string> = {
  ACTIVE: "Ativa",
  EXECUTED: "Executada",
  CANCELLED: "Cancelada",
  EXPIRED: "Expirada",
  FAILED: "Falhou",
};

function ScheduledActionsWidget({
  actions,
  agentIsRunning,
  sendUserMessage,
}: {
  actions: ScheduledActionItem[];
  agentIsRunning: boolean;
  sendUserMessage: (content: string) => Promise<unknown>;
}) {
  const [pendingAction, setPendingAction] = useState<string | null>(null);

  const money = (s: string) => {
    const n = parseFloat(s);
    return Number.isFinite(n)
      ? n.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      : s;
  };
  const dateFmt = (s: string | null) => {
    if (!s) return "—";
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Cancelar: envia uma mensagem no chat e deixa o agente disparar o
  // delete_scheduled_action com HITL (mesmo padrão do OpenOrdersWidget).
  const cancelAction = async (action: ScheduledActionItem) => {
    if (pendingAction || agentIsRunning) return;
    setPendingAction(action.id);
    try {
      await sendUserMessage(`Cancelar a action ${action.id}`);
    } finally {
      setPendingAction(null);
    }
  };

  const activeCount = actions.filter((a) => a.status === "ACTIVE").length;

  if (actions.length === 0) {
    return (
      <div className="scheduledWidget">
        <div className="scheduledWidget-head">
          <div className="scheduledWidget-icon">🎯</div>
          <div>
            <div className="scheduledWidget-title">Ações agendadas</div>
            <div className="scheduledWidget-subtitle">Alertas e ordens</div>
          </div>
        </div>
        <div className="scheduledWidget-empty">
          Você não tem ações agendadas.
        </div>
      </div>
    );
  }

  return (
    <div className="scheduledWidget">
      <div className="scheduledWidget-head">
        <div className="scheduledWidget-icon">🎯</div>
        <div>
          <div className="scheduledWidget-title">Ações agendadas</div>
          <div className="scheduledWidget-subtitle">
            {activeCount === 1 ? "1 ativa" : `${activeCount} ativas`}
            {actions.length > activeCount
              ? ` · ${actions.length} no total`
              : ""}
          </div>
        </div>
      </div>

      <div className="scheduledWidget-coins">
        {actions.map((action) => {
          return (
            <div className="scheduledWidget-coin" key={action.id}>
              <div className="scheduledWidget-coinHead">
                <span className="scheduledWidget-coinIcon">
                  {(action.baseCoin || "?").slice(0, 3)}
                </span>
                <span className="scheduledWidget-coinName">
                  {SCHEDULED_ACTION_TYPE_LABEL[action.type] ?? action.type}
                </span>
                <span className="scheduledWidget-status">
                  {SCHEDULED_ACTION_STATUS_LABEL[action.status] ??
                    action.status}
                </span>
              </div>
              <div className="scheduledWidget-coinRow">
                <span className="scheduledWidget-coinLabel">Gatilho</span>
                <span className="scheduledWidget-coinValue">
                  {action.direction === "BELOW" ? "abaixo de" : "acima de"}{" "}
                  {money(action.targetPrice)} {action.quoteCoin}
                </span>
              </div>
              {action.currentPrice && (
                <div className="scheduledWidget-coinRow">
                  <span className="scheduledWidget-coinLabel">Preço atual</span>
                  <span className="scheduledWidget-coinValue">
                    {money(action.currentPrice)} {action.quoteCoin}
                  </span>
                </div>
              )}
              <div className="scheduledWidget-coinRow">
                <span className="scheduledWidget-coinLabel">Recorrência</span>
                <span className="scheduledWidget-coinValue">
                  {action.recurring ? "Recorrente" : "Uma única vez"}
                </span>
              </div>
              <div className="scheduledWidget-coinRow">
                <span className="scheduledWidget-coinLabel">Expira em</span>
                <span className="scheduledWidget-coinValue">
                  {action.expiresAt ? dateFmt(action.expiresAt) : "Sem expiração"}
                </span>
              </div>
              {action.failureReason && (
                <div className="scheduledWidget-coinRow">
                  <span className="scheduledWidget-coinLabel">Falha</span>
                  <span className="scheduledWidget-coinValue">
                    {action.failureReason}
                  </span>
                </div>
              )}
              {action.status === "ACTIVE" && (
                <div className="scheduledWidget-actions">
                  <button
                    type="button"
                    className="scheduledWidget-cancelBtn"
                    onClick={() => void cancelAction(action)}
                    disabled={pendingAction !== null || agentIsRunning}
                  >
                    {pendingAction === action.id ? "Enviando…" : "Cancelar"}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Widget da tool `get_referral_code`: código, link de convite e contadores.
 *
 * Sem código (`hasCode: false`) não há nada para compartilhar — em vez de um
 * card vazio, o widget leva para a tela onde o usuário cria o dele. Os campos
 * vêm anuláveis do contrato justamente porque só existem quando há código, daí
 * o guard combinado em vez de confiar apenas no `hasCode`.
 */
function ReferralCodeWidget({
  output,
}: {
  output: McpTools["get_referral_code"]["output"];
}) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  if (!output.hasCode || !output.code || !output.link) {
    return (
      <div className="referralWidget">
        <div className="referralWidget-head">
          <div className="referralWidget-icon">🎁</div>
          <div>
            <div className="referralWidget-title">Convide e ganhe</div>
            <div className="referralWidget-sub">
              Você ainda não tem um código
            </div>
          </div>
        </div>

        <div className="referralWidget-hint">
          Crie seu código de indicação para gerar o link de convite.
        </div>

        <button
          type="button"
          className="referralWidget-btn referralWidget-btn--primary"
          onClick={() => navigate("/referral")}
        >
          Criar meu código
        </button>
      </div>
    );
  }

  const { code, link } = output;
  const total = output.totalReferrals ?? 0;
  const qualified = output.qualifiedReferrals ?? 0;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      console.warn("Falha ao copiar link de indicação:", err);
      toast.error("Não foi possível copiar. Copie manualmente o link acima.");
    }
  };

  const handleShare = async () => {
    if (!navigator.share) {
      await handleCopy();
      return;
    }

    try {
      await navigator.share({
        title: "wedofi",
        text: "Crie sua conta no wedofi com meu convite:",
        url: link,
      });
    } catch {
      // Compartilhamento cancelado pelo usuário — nada a fazer.
    }
  };

  return (
    <div className="referralWidget">
      <div className="referralWidget-head">
        <div className="referralWidget-icon">🎁</div>
        <div>
          <div className="referralWidget-title">Seu convite</div>
          <div className="referralWidget-sub">
            Código <strong>{code}</strong>
          </div>
        </div>
      </div>

      {output.enabled === false && (
        <div className="referralWidget-disabled">
          Este código está desativado — o link não aceita novos cadastros.
        </div>
      )}

      <div className="referralWidget-qr">
        <QRCodeSVG value={link} size={148} level="M" marginSize={2} />
      </div>

      <button
        type="button"
        className="referralWidget-linkBox"
        onClick={() => void handleCopy()}
        title="Copiar link de convite"
      >
        <span className="referralWidget-linkText">{link}</span>
        <span className="referralWidget-copyIcon" aria-hidden="true">
          {copied ? "✓" : "⧉"}
        </span>
      </button>

      <div className="referralWidget-actions">
        <button
          type="button"
          className="referralWidget-btn referralWidget-btn--primary"
          onClick={() => void handleCopy()}
        >
          {copied ? "Copiado!" : "Copiar link"}
        </button>
        <button
          type="button"
          className="referralWidget-btn referralWidget-btn--ghost"
          onClick={() => void handleShare()}
        >
          Compartilhar
        </button>
      </div>

      <div className="referralWidget-stats">
        <div className="referralWidget-stat">
          <span className="referralWidget-statValue">{total}</span>
          <span className="referralWidget-statLabel">
            {total === 1 ? "indicação" : "indicações"}
          </span>
        </div>
        <div className="referralWidget-stat">
          <span className="referralWidget-statValue referralWidget-statValue--ok">
            {qualified}
          </span>
          <span className="referralWidget-statLabel">
            {qualified === 1 ? "qualificada" : "qualificadas"}
          </span>
        </div>
      </div>

      {total > qualified && (
        <div className="referralWidget-hint">
          {total - qualified === 1
            ? "1 pessoa ainda não fez o depósito mínimo."
            : `${total - qualified} pessoas ainda não fizeram o depósito mínimo.`}
        </div>
      )}
    </div>
  );
}

const toolBodyRenderers: Record<string, ToolRenderer> = {
  add: ({ tool }) => {
    // `add` é uma tool de demonstração que pode não estar exposta no backend
    // atual (não faz parte de `McpTools`); lemos o output cru para o widget
    // não depender da chave gerada.
    const raw = tool.tool_end?.output;
    const output = raw && typeof raw === "object" && "structured_content" in raw
      ? (raw as { structured_content?: unknown }).structured_content
      : raw;
    if (!output) return null;
    return (
      <pre className="toolJson">
        {JSON.stringify(output, null, 2)}
      </pre>
    );
  },
  get_balance: ({ tool }) => {
    const input = getToolInput<"get_balance">(tool);
    const output = getToolOutput<"get_balance">(tool);
    console.log({tools: 'get_balance',input, output})
    if (!output) return null;

    // Os saldos/PNL chegam como string decimal (ou number, no contrato antigo).
    const num = (v: string | number | null | undefined): number | null => {
      if (v == null) return null;
      const n = typeof v === "number" ? v : parseFloat(v);
      return Number.isFinite(n) ? n : null;
    };

    const totalEquity = num(output.totalEquity);
    if (totalEquity === null) return null;

    const formatUSD = (n: number) =>
      n.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    const formatSignedPct = (n: number) =>
      `${n >= 0 ? "+" : ""}${n.toFixed(2)}%`;
    const formatSignedUSD = (n: number) =>
      `${n >= 0 ? "+" : "-"}$${formatUSD(Math.abs(n))}`;

    const totalWalletBalance = num(output.totalWalletBalance);
    const totalAvailableBalance = num(output.totalAvailableBalance);
    const pnlUSD = num(output.pnlUSD);
    const pnlPercent = num(output.pnlPercent);
    const pnl24hPercent = num(output.pnl24hPercent);
    const hasPnlStats = pnlUSD !== null || pnlPercent !== null ||
      pnl24hPercent !== null;

    const statClass = (n: number | null) =>
      `balanceWidget-stat-value ${
        n === null
          ? ""
          : n >= 0
          ? "balanceWidget-stat-value--up"
          : "balanceWidget-stat-value--down"
      }`;

    return (
      <div className="balanceWidget">
        <div className="balanceWidget-head">
          <div className="balanceWidget-icon">$</div>
          <div>
            <div className="balanceWidget-title">Saldo da conta</div>
          </div>
        </div>
        <div className="balanceWidget-amount">
          ${formatUSD(totalEquity)}
        </div>

        <div className="balanceWidget-stats">
          <div className="balanceWidget-stat">
            <span className="balanceWidget-stat-label">Equity total</span>
            <span className="balanceWidget-stat-value">
              ${formatUSD(totalEquity)}
            </span>
          </div>
          <div className="balanceWidget-stat">
            <span className="balanceWidget-stat-label">Saldo carteira</span>
            <span className="balanceWidget-stat-value">
              {totalWalletBalance !== null ? `$${formatUSD(totalWalletBalance)}` : "—"}
            </span>
          </div>
          <div className="balanceWidget-stat">
            <span className="balanceWidget-stat-label">Disponível</span>
            <span className="balanceWidget-stat-value">
              {totalAvailableBalance !== null ? `$${formatUSD(totalAvailableBalance)}` : "—"}
            </span>
          </div>
        </div>

        {hasPnlStats && (
          <div className="balanceWidget-stats">
            <div className="balanceWidget-stat">
              <span className="balanceWidget-stat-label">PNL</span>
              <span className={statClass(pnlUSD)}>
                {pnlUSD !== null ? formatSignedUSD(pnlUSD) : "—"}
              </span>
            </div>
            <div className="balanceWidget-stat">
              <span className="balanceWidget-stat-label">PNL total</span>
              <span className={statClass(pnlPercent)}>
                {pnlPercent !== null ? formatSignedPct(pnlPercent) : "—"}
              </span>
            </div>
            <div className="balanceWidget-stat">
              <span className="balanceWidget-stat-label">24h</span>
              <span className={statClass(pnl24hPercent)}>
                {pnl24hPercent !== null ? formatSignedPct(pnl24hPercent) : "—"}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  },
  get_quote: ({ tool }) => {
    const output = getToolOutput<"get_quote">(tool);

    if (!output || typeof output !== "object" || !output.symbol) return null;

    const isPositive = parseFloat(output.price24hPcnt) >= 0;
    const label = SYMBOL_LABELS[output.symbol] ?? {
      name: output.baseCoin,
      icon: output.baseCoin,
    };

    return (
      <div className="quoteWidget">
        <div className="quoteWidget-head">
          <div className="quoteWidget-badge">{label.icon}</div>
          <div>
            <div className="quoteWidget-symbol">{output.symbol}</div>
            <div className="quoteWidget-name">{label.name}</div>
          </div>
          <div
            className={`quoteWidget-change ${
              isPositive ? "quoteWidget-change--up" : "quoteWidget-change--down"
            }`}
          >
            {formatPercent(output.price24hPcnt)}
          </div>
        </div>
        <div className="quoteWidget-price">${formatPrice(output.price)}</div>

        {output.chartData && output.chartData.length > 1 && (
          <Sparkline data={output.chartData} isPositive={isPositive} />
        )}

        <div className="quoteWidget-details">
          <div className="quoteWidget-detail">
            <span className="quoteWidget-detail-label">High 24h</span>
            <span className="quoteWidget-detail-value">
              ${formatPrice(output.highPrice24h)}
            </span>
          </div>
          <div className="quoteWidget-detail">
            <span className="quoteWidget-detail-label">Low 24h</span>
            <span className="quoteWidget-detail-value">
              ${formatPrice(output.lowPrice24h)}
            </span>
          </div>
          <div className="quoteWidget-detail">
            <span className="quoteWidget-detail-label">Vol 24h</span>
            <span className="quoteWidget-detail-value">
              {formatVolume(output.volume24h)}
            </span>
          </div>
        </div>
      </div>
    );
  },
  get_fear_greed_index: ({ tool }) => {
    const output = getToolOutput<"get_fear_greed_index">(tool);
    if (!output) return null;

    const CODE_COLORS: Record<
      string,
      { bg: string; text: string; gradient: string }
    > = {
      extreme_fear: {
        bg: "rgba(239,68,68,0.12)",
        text: "#dc2626",
        gradient: "linear-gradient(135deg,#ef4444,#dc2626)",
      },
      fear: {
        bg: "rgba(249,115,22,0.12)",
        text: "#c2410c",
        gradient: "linear-gradient(135deg,#f97316,#ea580c)",
      },
      neutral: {
        bg: "rgba(234,179,8,0.12)",
        text: "#a16207",
        gradient: "linear-gradient(135deg,#eab308,#ca8a04)",
      },
      greed: {
        bg: "rgba(34,197,94,0.12)",
        text: "#15803d",
        gradient: "linear-gradient(135deg,#22c55e,#16a34a)",
      },
      extreme_greed: {
        bg: "rgba(16,185,129,0.12)",
        text: "#065f46",
        gradient: "linear-gradient(135deg,#10b981,#059669)",
      },
    };

    const colors = CODE_COLORS[output.code] ?? CODE_COLORS.neutral;
    const pct = Math.min(100, Math.max(0, output.value));

    return (
      <div className="fgiWidget">
        <div className="fgiWidget-head">
          <div
            className="fgiWidget-icon"
            style={{ background: colors.gradient }}
          >
            FGI
          </div>
          <div>
            <div className="fgiWidget-title">Fear & Greed Index</div>
            <div className="fgiWidget-timestamp">
              {new Date(output.timestamp).toLocaleDateString("pt-BR", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </div>
          </div>
        </div>

        <div className="fgiWidget-value" style={{ color: colors.text }}>
          {output.value}
        </div>

        <div className="fgiWidget-bar">
          <div
            className="fgiWidget-barFill"
            style={{ width: `${pct}%`, background: colors.gradient }}
          />
        </div>
        <div className="fgiWidget-barLabels">
          <span>0 — Medo Extremo</span>
          <span>100 — Ganância Extrema</span>
        </div>

        <div
          className="fgiWidget-label"
          style={{ background: colors.bg, color: colors.text }}
        >
          {output.label}
        </div>
      </div>
    );
  },
  navigate_to: ({ tool }) => {
    const output = getToolOutput<"navigate_to">(tool);
    if (!output) return null;
    // TODO: implementar widget
    return null;
  },
  get_referral_code: ({ tool }) => {
    const output = getToolOutput<"get_referral_code">(tool);
    if (!output) return null;
    return <ReferralCodeWidget output={output} />;
  },
  update_user_settings: ({ tool }) => {
    const output = getUpdateUserSettingsOutput(tool);
    if (!output) return null;

    const input = getUpdateUserSettingsInput(tool);

    return <UserSettingsUpdatedWidget input={input} output={output} />;
  },
  get_deposit_address: ({ tool }) => {
    const output = getToolOutput<"get_deposit_address">(tool);
    if (!output) return null;
    return <DepositAddressWidget output={output} />;
  },
  list_deposit_networks: ({ tool, sendUserMessage, agentIsRunning }) => {
    const input = getToolInput<"list_deposit_networks">(tool);
    const output = getToolOutput<"list_deposit_networks">(tool);
    if (!output) return null;
    return (
      <NetworksWidget
        kind="deposit"
        coin={input?.coin ?? ""}
        networks={output.networks}
        sendUserMessage={sendUserMessage}
        agentIsRunning={agentIsRunning}
      />
    );
  },
  list_withdraw_networks: ({ tool, sendUserMessage, agentIsRunning }) => {
    const input = getToolInput<"list_withdraw_networks">(tool);
    const output = getToolOutput<"list_withdraw_networks">(tool);
    if (!output) return null;
    return (
      <NetworksWidget
        kind="withdraw"
        coin={input?.coin ?? ""}
        networks={output.networks}
        sendUserMessage={sendUserMessage}
        agentIsRunning={agentIsRunning}
      />
    );
  },
  list_orders: ({ tool, agentIsRunning, sendUserMessage }) => {
    const output = getListOrdersOutput(tool);
    if (!output) return null;
    const input = getListOrdersInput(tool);
    return (
      <OpenOrdersWidget
        output={output}
        input={input}
        agentIsRunning={agentIsRunning}
        sendUserMessage={sendUserMessage}
      />
    );
  },
  wallet_history: ({ tool, agentIsRunning, sendUserMessage }) => {
    const output = getWalletHistoryOutput(tool);
    if (!output) return null;
    const input = getWalletHistoryInput(tool);
    return (
      <WalletHistoryWidget
        output={output}
        input={input}
        agentIsRunning={agentIsRunning}
        sendUserMessage={sendUserMessage}
      />
    );
  },
  get_portfolio_coin: ({ tool }) => {
    const output = getToolOutput<"get_portfolio_coin">(tool);
    if (!output) return null;

    const formatUSD = (v: string | number) =>
      toNumber(v).toLocaleString("pt-BR", { style: "currency", currency: "USD" });
    const formatQty = (v: string | number) =>
      toNumber(v).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 8,
      });
    const formatSignedPct = (v: string | number) => {
      const n = toNumber(v);
      return `${n >= 0 ? "+" : ""}${n.toFixed(2)}%`;
    };

    const perfUp = toNumber(output.performancePercent) >= 0;

    return (
      <div className="coinWidget">
        <div className="coinWidget-hero">
          <div className="coinWidget-heroHead">
            <div className="coinWidget-heroIcon">{output.coin.slice(0, 3)}</div>
            <div className="coinWidget-heroName">{output.coin}</div>
          </div>
          <div className="coinWidget-heroAmount">
            <span className="coinWidget-heroAmount-approx">≈</span>
            {formatUSD(output.totalUSD)}
          </div>
          <div className="coinWidget-heroCard">
            <div className="coinWidget-heroCardCell">
              <div className="coinWidget-heroCardLabel">Saldo</div>
              <div className="coinWidget-heroCardValue">
                {formatQty(output.quantity)} {output.coin}
              </div>
            </div>
            <div className="coinWidget-heroCardCell">
              <div className="coinWidget-heroCardLabel">Performance</div>
              <div
                className={`coinWidget-heroCardValue ${
                  perfUp
                    ? "coinWidget-heroCardValue--up"
                    : "coinWidget-heroCardValue--down"
                }`}
              >
                {perfUp ? "↑" : "↓"} {formatSignedPct(output.performancePercent)}
              </div>
            </div>
          </div>
        </div>

        <div className="coinWidget-details">
          <div className="coinWidget-detailRow">
            <span className="coinWidget-detailLabel">
              Disponível p/ converter/sacar
            </span>
            <span className="coinWidget-detailValue">
              {formatQty(output.available)} {output.coin}
              {toNumber(output.available) < toNumber(output.quantity) && (
                <span className="coinWidget-detailHint">
                  {formatQty(toNumber(output.quantity) - toNumber(output.available))} em ordens/margem
                </span>
              )}
            </span>
          </div>
          <div className="coinWidget-detailRow">
            <span className="coinWidget-detailLabel">Preço médio de compra</span>
            <span className="coinWidget-detailValue">
              {formatUSD(output.averageBuyPrice)}
            </span>
          </div>
          <div className="coinWidget-detailRow">
            <span className="coinWidget-detailLabel">Total investido</span>
            <span className="coinWidget-detailValue">
              {formatUSD(output.totalInvested)}
            </span>
          </div>
          <div className="coinWidget-detailRow">
            <span className="coinWidget-detailLabel">Performance</span>
            <span className="coinWidget-detailValue">
              <span
                className={perfUp
                  ? "coinWidget-detailValue-accent--up"
                  : "coinWidget-detailValue-accent--down"}
              >
                ({formatSignedPct(output.performancePercent)})
              </span>
              {formatUSD(output.performance)}
            </span>
          </div>
        </div>
      </div>
    );
  },
  get_dcai_pnl: ({ tool }) => {
    const output = getToolOutput<"get_dcai_pnl">(tool);
    if (!output) return null;

    // Os valores monetários/quantidades chegam como string (precisão do BTC).
    // Convertemos só para formatar; nulls viram "—" sem quebrar o widget.
    const num = (s: string | null | undefined): number | null => {
      if (s == null) return null;
      const n = parseFloat(s);
      return Number.isFinite(n) ? n : null;
    };
    const quote = output.quoteCoin ?? "USDT";
    const money = (n: number) =>
      n.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    const qtyFmt = (n: number) =>
      n.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 8,
      });
    const signedPct = (n: number) => `${n >= 0 ? "+" : ""}${n.toFixed(2)}%`;
    const signedMoney = (n: number) =>
      `${n >= 0 ? "+" : "-"}${money(Math.abs(n))} ${quote}`;

    // Sem posição custodiada: nada a exibir além de um aviso amigável.
    if (!output.hasPosition || output.byCoin.length === 0) {
      return (
        <div className="dcaiWidget">
          <div className="dcaiWidget-head">
            <div className="dcaiWidget-icon">🤖</div>
            <div>
              <div className="dcaiWidget-title">DCAi</div>
              <div className="dcaiWidget-subtitle">Desempenho</div>
            </div>
          </div>
          <div className="dcaiWidget-empty">
            Você ainda não tem uma posição de DCAi ativa.
          </div>
        </div>
      );
    }

    const marketValue = num(output.marketValue);
    const invested = num(output.invested);
    const pnl = num(output.unrealizedPnl);
    const pnlPercent = num(output.pnlPercent);
    const pnlUp = pnl === null ? null : pnl >= 0;

    return (
      <div className="dcaiWidget">
        <div className="dcaiWidget-head">
          <div className="dcaiWidget-icon">🤖</div>
          <div>
            <div className="dcaiWidget-title">DCAi</div>
            <div className="dcaiWidget-subtitle">Desempenho</div>
          </div>
        </div>

        <div className="dcaiWidget-amount">
          <span className="dcaiWidget-amount-approx">≈</span>
          {marketValue !== null ? `${money(marketValue)} ${quote}` : "—"}
        </div>
        <div className="dcaiWidget-amount-label">
          Valor atual do acumulado
        </div>

        <div className="dcaiWidget-stats">
          <div className="dcaiWidget-stat">
            <span className="dcaiWidget-stat-label">Total investido</span>
            <span className="dcaiWidget-stat-value">
              {invested !== null ? `${money(invested)} ${quote}` : "—"}
            </span>
          </div>
          <div className="dcaiWidget-stat">
            <span className="dcaiWidget-stat-label">Resultado</span>
            <span
              className={`dcaiWidget-stat-value ${
                pnlUp === null
                  ? ""
                  : pnlUp
                  ? "dcaiWidget-stat-value--up"
                  : "dcaiWidget-stat-value--down"
              }`}
            >
              {pnl !== null ? signedMoney(pnl) : "—"}
            </span>
          </div>
          <div className="dcaiWidget-stat">
            <span className="dcaiWidget-stat-label">Performance</span>
            <span
              className={`dcaiWidget-stat-value ${
                pnlUp === null
                  ? ""
                  : pnlUp
                  ? "dcaiWidget-stat-value--up"
                  : "dcaiWidget-stat-value--down"
              }`}
            >
              {pnlPercent !== null
                ? `${pnlUp ? "↑" : "↓"} ${signedPct(pnlPercent)}`
                : "—"}
            </span>
          </div>
        </div>

        <div className="dcaiWidget-coins">
          {output.byCoin.map((coin) => {
            const qty = num(coin.qty);
            const avg = num(coin.avgBuyPrice);
            return (
              <div className="dcaiWidget-coin" key={coin.baseCoin}>
                <div className="dcaiWidget-coinHead">
                  <span className="dcaiWidget-coinIcon">
                    {coin.baseCoin.slice(0, 3)}
                  </span>
                  <span className="dcaiWidget-coinName">{coin.baseCoin}</span>
                </div>
                <div className="dcaiWidget-coinRow">
                  <span className="dcaiWidget-coinLabel">Acumulado</span>
                  <span className="dcaiWidget-coinValue">
                    {qty !== null ? `${qtyFmt(qty)} ${coin.baseCoin}` : "—"}
                  </span>
                </div>
                <div className="dcaiWidget-coinRow">
                  <span className="dcaiWidget-coinLabel">
                    Preço médio de compra
                  </span>
                  <span className="dcaiWidget-coinValue">
                    {avg !== null ? `${money(avg)} ${coin.quoteCoin}` : "—"}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {output.partial && (
          <div className="dcaiWidget-note">
            Cotação indisponível para parte da posição — alguns valores podem
            estar incompletos.
          </div>
        )}
      </div>
    );
  },
  list_dcai_plans: ({ tool }) => {
    const output = getListDcaiPlansOutput(tool);
    if (!output) return null;
    return <DcaiPlansWidget plans={output.plans ?? []} />;
  },
  list_scheduled_actions: ({ tool, sendUserMessage, agentIsRunning }) => {
    const output = getListScheduledActionsOutput(tool);
    if (!output || !Array.isArray(output.actions)) return null;
    return (
      <ScheduledActionsWidget
        actions={output.actions}
        agentIsRunning={agentIsRunning}
        sendUserMessage={sendUserMessage}
      />
    );
  },
  create_scheduled_action: ({ tool }) => {
    // Recibo compacto pós-execução; rejeição/erro já são narrados pelo card
    // de aprovação HITL — sem card duplicado na timeline.
    const result = getScheduledActionToolResult(tool);
    if (result?.status !== "executed" || !result.action) return null;
    const action = result.action;
    return (
      <div className="scheduledWidget">
        <div className="scheduledWidget-head">
          <div className="scheduledWidget-icon">🎯</div>
          <div>
            <div className="scheduledWidget-title">Ação agendada criada</div>
            <div className="scheduledWidget-subtitle">
              {SCHEDULED_ACTION_TYPE_LABEL[action.type] ?? action.type} —{" "}
              {action.baseCoin}
            </div>
          </div>
        </div>
        <div className="scheduledWidget-coin">
          <div className="scheduledWidget-coinRow">
            <span className="scheduledWidget-coinLabel">Gatilho</span>
            <span className="scheduledWidget-coinValue">
              {action.direction === "BELOW" ? "abaixo de" : "acima de"}{" "}
              {action.targetPrice} {action.quoteCoin}
            </span>
          </div>
        </div>
      </div>
    );
  },
  delete_scheduled_action: ({ tool }) => {
    const result = getScheduledActionToolResult(tool);
    if (result?.status !== "executed" || !result.action) return null;
    return (
      <div className="scheduledWidget">
        <div className="scheduledWidget-head">
          <div className="scheduledWidget-icon">🎯</div>
          <div>
            <div className="scheduledWidget-title">Ação agendada excluída</div>
            <div className="scheduledWidget-subtitle scheduledWidget-mono">
              {result.action.id}
            </div>
          </div>
        </div>
      </div>
    );
  },
  get_portfolio: ({ tool }) => {
    const output = getToolOutput<"get_portfolio">(tool);
    if (!output || !Array.isArray(output.items)) return null;

    const items = output.items;
    if (items.length === 0) {
      return (
        <div className="portfolioWidget">
          <div className="portfolioWidget-empty">Nenhum ativo na carteira.</div>
        </div>
      );
    }

    const formatUSD = (v: string | number) =>
      toNumber(v).toLocaleString("pt-BR", { style: "currency", currency: "USD" });
    const formatQty = (v: string | number) =>
      toNumber(v).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 8,
      });
    const formatSignedPct = (v: string | number) => {
      const n = toNumber(v);
      return `${n >= 0 ? "+" : ""}${n.toFixed(2)}%`;
    };

    return (
      <div className="portfolioWidget">
        <div className="portfolioWidget-head">
          <div className="portfolioWidget-icon" aria-hidden="true">💼</div>
          <div className="portfolioWidget-headText">
            <div className="portfolioWidget-title">Carteira</div>
            <div className="portfolioWidget-sub">
              {items.length} {items.length === 1 ? "ativo" : "ativos"}
            </div>
          </div>
        </div>

        <ul className="portfolioWidget-list">
          {items.map((item) => {
            const isUp = toNumber(item.change24hPercent) >= 0;
            return (
              <li key={item.coin} className="portfolioWidget-row">
                <div className="portfolioWidget-rowIcon" aria-hidden="true">
                  {item.coin.slice(0, 3)}
                </div>
                <div className="portfolioWidget-rowInfo">
                  <div className="portfolioWidget-rowCode">{item.coin}</div>
                  <div className="portfolioWidget-rowSub">
                    <span>{formatUSD(item.priceUSD)}</span>
                    <span
                      className={
                        isUp
                          ? "portfolioWidget-change--up"
                          : "portfolioWidget-change--down"
                      }
                    >
                      {formatSignedPct(item.change24hPercent)}
                    </span>
                  </div>
                </div>
                <div className="portfolioWidget-rowValues">
                  <div className="portfolioWidget-rowQty">
                    {formatQty(item.equity)} {item.coin}
                  </div>
                  <div className="portfolioWidget-rowTotal">
                    {formatUSD(item.totalUSD)}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    );
  },
};

function unwrapEventData(x: any) {
  if (x && typeof x === "object" && x.data && typeof x.data === "object") {
    return x.data;
  }

  return x;
}

function ToolCardShell({
  tool,
  isExpanded,
  onToggleExpanded,
  children,
}: {
  tool: ChatTool;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  children?: ReactNode;
}) {
  return (
    <div key={tool.id} className="toolWrap">
      <div
        className={`toolCard${tool.status === "interrupt" ? " toolCard--interrupt" : ""}`}
        role="group"
        aria-label="Execução de ferramenta"
      >
        <div className="toolHeader">
          <div className="toolHeaderLeft">
            <span className="toolBadge" aria-hidden="true">
              TOOL
            </span>
            <span className="toolTime">{new Date(tool.started_at).toLocaleTimeString()}</span>
          </div>
          <span className={`toolStatusPill toolStatusPill-${tool.status}`}>
            {tool.status === "running"
              ? "Executando"
              : tool.status === "done"
                ? "Concluído"
                : tool.status === "interrupt"
                  ? "Aguardando"
                  : "Erro"}
          </span>
        </div>

        <div className="toolRow">
          {tool.status === "running" ? (
            <span className="toolSpinner" aria-label="Executando" />
          ) : (
            <span
              className={`toolDot toolDot-${tool.status === "done" ? "done" : tool.status === "interrupt" ? "interrupt" : "error"}`}
              aria-label={tool.status === "done" ? "Concluído" : tool.status === "interrupt" ? "Aguardando" : "Erro"}
            />
          )}

          <span className="toolName">{tool.name}</span>

          <button
            type="button"
            className="toolExpandBtn"
            aria-expanded={isExpanded}
            disabled={tool.status === "running"}
            onClick={onToggleExpanded}
            title={isExpanded ? "Recolher detalhes" : "Expandir detalhes"}
          >
            <svg className={`toolChevron ${isExpanded ? "toolChevronOpen" : ""}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {children}

        {isExpanded ? (
          <pre className="toolJson">
            {JSON.stringify({ tool_start: tool.tool_start, tool_end: tool.tool_end, events: tool.events }, null, 2)}
          </pre>
        ) : null}
      </div>
    </div>
  );
}

function ToolDefaultRenderer(ctx: ToolRendererCtx) {
  const body = toolBodyRenderers[ctx.tool.name]?.(ctx) ?? null;
  return (
    <ToolCardShell tool={ctx.tool} isExpanded={ctx.isExpanded} onToggleExpanded={ctx.onToggleExpanded}>
      {ctx.children}
      {body}
    </ToolCardShell>
  );
}

function ToolAddRenderer(ctx: ToolRendererCtx) {
  const { tool } = ctx;

  const lastSeenToolMessageCountRef = useRef<number | null>(null);

  const toolMessages = useMemo(() => {
    const events = (tool.events ?? []) as any[];
    return events.filter((e) => e?.event === "tool_message");
  }, [tool.events]);

  useEffect(() => {
    if (lastSeenToolMessageCountRef.current === null) {
      // Não dispara toast para eventos já existentes (histórico/reload).
      lastSeenToolMessageCountRef.current = toolMessages.length;
      return;
    }

    const seen = lastSeenToolMessageCountRef.current;

    if (toolMessages.length <= seen) {
      return;
    }

    const newOnes = toolMessages.slice(seen);

    for (const ev of newOnes) {
      const d = unwrapEventData(ev?.data);
      const level = String(d?.level ?? "info").toLowerCase();
      const message = String(d?.message ?? "").trim() || "Tool message";
      const extraState = d?.extra?.state;
      const description = typeof extraState === "string" ? `state: ${extraState}` : undefined;

      if (level === "error") toast.error(message, { description });
      else if (level === "warn" || level === "warning") toast.warning(message, { description });
      else if (level === "debug") toast(message, { description });
      else toast.info(message, { description });
    }

    lastSeenToolMessageCountRef.current = toolMessages.length;
  }, [toolMessages]);

  const progressEvent = useMemo(() => {
    const events = (tool.events ?? []) as any[];
    for (let i = events.length - 1; i >= 0; i--) {
      const e = events[i];
      if (e?.event === "tool_progress") return e;
    }
    return null;
  }, [tool.events]);

  const progressData: any = unwrapEventData(progressEvent?.data ?? null);
  const progressTotal = typeof progressData?.total === "number" ? progressData.total : null;
  const progressValue = typeof progressData?.progress === "number" ? progressData.progress : null;
  const progressPct =
    progressTotal && progressTotal > 0 && typeof progressValue === "number"
      ? Math.max(0, Math.min(100, (progressValue / progressTotal) * 100))
      : null;
  const progressLabel = typeof progressData?.message === "string" ? progressData.message : null;

  const body = toolBodyRenderers.add?.(ctx) ?? null;

  return (
    <ToolCardShell tool={tool} isExpanded={ctx.isExpanded} onToggleExpanded={ctx.onToggleExpanded}>
      {progressPct !== null ? (
        <div className="toolProgressWrap" aria-label="Progresso da ferramenta">
          <div className={`toolProgressBar toolProgressBar-${tool.status}`}>
            <div className="toolProgressFill" style={{ width: `${progressPct}%` }} />
          </div>
          <div className="toolProgressMeta">
            <span className="toolProgressPct">{Math.round(progressPct)}%</span>
            {progressLabel ? <span className="toolProgressText">{progressLabel}</span> : null}
          </div>
        </div>
      ) : null}

      {ctx.children}
      {body}
    </ToolCardShell>
  );
}

const toolRenderers: Record<string, (ctx: ToolRendererCtx) => ReactNode> = {
  add: ToolAddRenderer,
};

/**
 * Isola a renderização de cada widget de tool. Um output inesperado (ex.: campo
 * ausente durante o streaming, contrato divergente do backend) não deve derrubar
 * a página inteira via ErrorBoundary do router — aqui degrada para um cartão de
 * erro contido no próprio widget.
 */
class ToolErrorBoundary extends Component<
  { toolName: string; children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(
      `Erro ao renderizar o widget da tool "${this.props.toolName}":`,
      error,
      info,
    );
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="toolWrap">
          <div className="toolHeader">
            <div className="toolHeaderLeft">
              <span className="toolName">{this.props.toolName}</span>
            </div>
          </div>
          <div className="depositWidget-hint">
            Não foi possível exibir este resultado.
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export function renderToolRun(ctx: ToolRendererCtx) {
  const Renderer = toolRenderers[ctx.tool.name] ?? ToolDefaultRenderer;

  return (
    <ToolErrorBoundary toolName={ctx.tool.name}>
      <Renderer {...ctx} />
    </ToolErrorBoundary>
  );
}
